import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { format } from 'date-fns';

const ACTION_COLORS = {
  login:      'bg-green-100 text-green-700 border-green-300',
  logout:     'bg-gray-100 text-gray-600 border-gray-300',
  approve:    'bg-blue-100 text-blue-700 border-blue-300',
  reject:     'bg-red-100 text-red-700 border-red-300',
  objection:  'bg-orange-100 text-orange-700 border-orange-300',
  forward:    'bg-indigo-100 text-indigo-700 border-indigo-300',
  default:    'bg-purple-100 text-purple-700 border-purple-300',
};

const ACTION_ICONS = {
  login: '🟢', logout: '🔴', approve: '✅',
  reject: '❌', objection: '⚠️', forward: '➡️',
};

export default function ActivityLogs() {
  const [logs, setLogs]     = useState([]);
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    emp_id: '', action: '', date_from: '', date_to: '', page: 1, limit: 50
  });
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [logRes, statsRes] = await Promise.all([
        api.get(`/activity-logs?${params}`),
        api.get('/activity-logs/stats'),
      ]);
      setLogs(logRes.logs || []);
      setTotal(logRes.total || 0);
      setStats(statsRes.stats || null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/masters/employees').then(r => setEmployees(r.employees || []));
    load();
  }, []);

  const f = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));

  const actionColor = (action) => ACTION_COLORS[action] || ACTION_COLORS.default;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">📋 Activity Logs</h1>
        <p className="text-sm text-gray-500 mt-1">Employee login history and actions</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="card text-center">
            <p className="text-3xl font-bold text-green-600">{stats.today_logins}</p>
            <p className="text-xs text-gray-500 mt-1">Today's Logins</p>
          </div>
          <div className="card text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.active_today}</p>
            <p className="text-xs text-gray-500 mt-1">Active Today</p>
          </div>
          <div className="card text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.total_logins}</p>
            <p className="text-xs text-gray-500 mt-1">Total Logins</p>
          </div>
          <div className="card text-center">
            <p className="text-3xl font-bold text-gray-700">{total}</p>
            <p className="text-xs text-gray-500 mt-1">Total Records</p>
          </div>
        </div>
      )}

      {/* Recent Logins */}
      {stats?.recent_logins?.length > 0 && (
        <div className="card mb-6">
          <h3 className="font-semibold text-gray-700 text-sm mb-3">🕐 Recent Logins</h3>
          <div className="space-y-2">
            {stats.recent_logins.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-xs flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  <span className="font-medium text-gray-800">{l.emp_name}</span>
                  <span className="text-gray-400">({l.login_id})</span>
                  <span className="text-gray-500">{l.role_code}</span>
                  {l.dept_name && <span className="text-blue-600">{l.dept_name}</span>}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  {l.ip_address && <span>📍 {l.ip_address}</span>}
                  <span>{l.created_at ? format(new Date(l.created_at), 'dd MMM, hh:mm a') : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <select className="form-select text-sm" value={filters.action} onChange={e => f('action', e.target.value)}>
            <option value="">All Actions</option>
            <option value="login">🟢 Login</option>
            <option value="logout">🔴 Logout</option>
            <option value="approve">✅ Approve</option>
            <option value="reject">❌ Reject</option>
            <option value="objection">⚠️ Objection</option>
            <option value="forward">➡️ Forward</option>
          </select>
          <select className="form-select text-sm" value={filters.emp_id} onChange={e => f('emp_id', e.target.value)}>
            <option value="">All Employees</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.emp_name} ({e.login_id})</option>
            ))}
          </select>
          <input type="date" className="form-input text-sm" value={filters.date_from}
            onChange={e => f('date_from', e.target.value)} placeholder="From date" />
          <input type="date" className="form-input text-sm" value={filters.date_to}
            onChange={e => f('date_to', e.target.value)} placeholder="To date" />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={load} className="btn-primary text-sm">🔍 Search</button>
          <button onClick={() => { setFilters({ emp_id:'', action:'', date_from:'', date_to:'', page:1, limit:50 }); setTimeout(load,100); }}
            className="btn-secondary text-sm">Reset</button>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">{total} records found</p>

      {/* Logs Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : logs.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">📭</p>
          <p className="text-lg font-semibold text-gray-700">No logs found</p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Login ID</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>IP Address</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="font-medium text-sm">{log.emp_name}</td>
                    <td className="text-xs text-gray-500 font-mono">{log.login_id}</td>
                    <td className="text-xs capitalize">{log.role_code}</td>
                    <td className="text-xs text-gray-500">{log.dept_name || '—'}</td>
                    <td>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${actionColor(log.action)}`}>
                        {ACTION_ICONS[log.action] || '•'} {log.action}
                      </span>
                    </td>
                    <td className="text-xs text-gray-500">{log.description || '—'}</td>
                    <td className="text-xs text-gray-400 font-mono">{log.ip_address || '—'}</td>
                    <td className="text-xs text-gray-500">
                      {log.created_at ? format(new Date(log.created_at), 'dd/MM/yy hh:mm a') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > filters.limit && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Page {filters.page} of {Math.ceil(total / filters.limit)}
              </p>
              <div className="flex gap-2">
                <button disabled={filters.page <= 1}
                  onClick={() => f('page', filters.page - 1)}
                  className="btn-secondary text-xs disabled:opacity-40">← Prev</button>
                <button disabled={filters.page >= Math.ceil(total / filters.limit)}
                  onClick={() => f('page', filters.page + 1)}
                  className="btn-secondary text-xs disabled:opacity-40">Next →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}